import React, { FC } from 'react';
import { Button, Drawer, Dropdown } from 'antd';
import {
  Notification,
  Divider,
  IconBackground,
  CountBadge,
  CardCollapse,
  Descriptions,
  DescriptionsRow,
} from 'common/components';
import { EColor } from 'common/const';
import { useInventoryContext } from 'common/context';
import { LIST_LIMIT_0 } from 'common/config';
import { countFormatter, dateFormatter } from 'common/helpers';
import { ArrowDownShort, Copy, LinkedUser } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { InventoryAssigneeUnassignBtn } from 'entities/Inventory/components';
import {
  EInventoryActionReason,
  EInventoryActionReasonLabel,
  EInventoryActionType,
  EInventoryStatus,
} from 'entities/Inventory/Inventory.enum';
import { getInventoryCardNotificationMessage } from 'entities/Inventory/Inventory.helper';
import { getUserName } from 'entities/User/User.helper';
import { GoodsCardMin } from 'entities/Goods/components';
import {
  addInventoryAssignee,
  filterInventoryActions,
  filterInventoryListItemActions,
  getInventoryAssignees,
  getInventoryUsers,
  removeInventoryAssignee,
  selectInventory,
  selectIsInventoryLoading,
} from 'entities/Inventory/Inventory.slice';
import { deleteInventoryAction } from 'entities/InventoryAction/InventoryAction.slice';
import { getUserInventoryStats, selectCurrentUser } from 'entities/User/User.slice';
import { IAddInventoryAssigneePayload, IRemoveInventoryAssigneePayload } from 'entities/Inventory/Inventory.models';

export const InventoryCard: FC = () => {
  const dispatch = useAppDispatch();
  // inventory
  const inventory = useAppSelector(selectInventory);
  const isInventoryLoading = useAppSelector(selectIsInventoryLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const {
    setIsNestedCard,
    openInventoryCard,
    setOpenInventoryCard,
    setOpenInventoryTransferCard,
    setOpenInventoryWriteOffCard,
    setOpenInventoryAssigneeCard,
  } = useInventoryContext();

  const isAvailableInventory = inventory?.status === EInventoryStatus.Available;
  const isWriteOffInventory = inventory?.status === EInventoryStatus.WriteOff;
  const transferRequests = inventory?.actions.filter((action) => action.type === EInventoryActionType.Transfer);
  const writeOffRequests = inventory?.actions.filter((action) => action.type === EInventoryActionType.WriteOff);

  const onClose = () => {
    if (isInventoryLoading) {
      return;
    }

    setOpenInventoryCard(false);
  };

  const onTransferInventoryClick = async () => {
    await dispatch(getInventoryUsers({ limit: LIST_LIMIT_0 }));
    setIsNestedCard(true);
    setOpenInventoryTransferCard(true);
  };

  const onWriteOffInventoryClick = () => {
    setIsNestedCard(true);
    setOpenInventoryWriteOffCard(true);
  };

  const onAssignInventoryClick = async () => {
    await dispatch(getInventoryAssignees({ limit: LIST_LIMIT_0 }));
    setIsNestedCard(true);
    setOpenInventoryAssigneeCard(true);
  };

  const onCancelInventoryActionClick = (id: number) => {
    if (inventory) {
      dispatch(
        deleteInventoryAction({
          ids: [id],
          onSuccess: () => {
            dispatch(filterInventoryActions(id));
            dispatch(filterInventoryListItemActions({ inventoryId: inventory.id, id }));
            dispatch(getUserInventoryStats({}));
          },
        }),
      );
    }
  };

  const copySellerCode = (e: any) => {
    e.stopPropagation();

    if (!inventory?.good?.sellerCode) {
      return;
    }

    navigator.clipboard.writeText(inventory.good.sellerCode);
  };

  const handleAddInventoryAssignee = async (payload: IAddInventoryAssigneePayload): Promise<void> => {
    await dispatch(addInventoryAssignee(payload));
  };

  const handleRemoveInventoryAssignee = async (payload: IRemoveInventoryAssigneePayload): Promise<void> => {
    await dispatch(removeInventoryAssignee(payload));
  };

  return (
    <Drawer
      rootClassName="redesign drawer"
      open={openInventoryCard}
      onClose={onClose}
      width={800}
      destroyOnClose
      classNames={{ footer: 'flex align-center justify-between' }}
      footer={
        isAvailableInventory
          ? [
              <Button
                key="writeOff"
                className="button-lg secondary"
                onClick={onWriteOffInventoryClick}
                loading={isInventoryLoading}
              >
                Списать оборудование
              </Button>,
              <Dropdown
                key="otherActions"
                overlayClassName="redesign"
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'transfer', label: 'Передать инструмент', onClick: onTransferInventoryClick },
                    { type: 'divider' },
                    { key: 'assign', label: 'Закрепить за сотрудником', onClick: onAssignInventoryClick },
                  ],
                }}
                placement="bottomRight"
                disabled={!!inventory?.user && inventory?.user.id !== currentUser?.id}
              >
                <Button className="button-lg primary" loading={isInventoryLoading}>
                  <span>Передать или закрепить</span>
                  <ArrowDownShort className="icon-arrow-down-short-white" />
                </Button>
              </Dropdown>,
            ]
          : null
      }
    >
      <div className="drawer__body">
        <div className="ff-regular fs-xxl mb-14">{inventory?.good.name}</div>

        <div className="mb-9">
          <GoodsCardMin good={inventory?.good} canBeOpened={true} />
        </div>

        <div className="mb-9">
          <CardCollapse
            keys={['info']}
            items={[
              {
                key: 'info',
                label: <span className="ff-medium fs-m">Информация об учёте</span>,
                children: (
                  <Descriptions>
                    {inventory?.good?.sellerCode && (
                      <DescriptionsRow
                        label="Артикул"
                        value={
                          <span className="flex align-center ff-regular fs-s">
                            {inventory.good.sellerCode}
                            <Copy className="ml-1 icon-dark-grey cursor-pointer" onClick={copySellerCode} />
                          </span>
                        }
                      />
                    )}
                    {isAvailableInventory && (
                      <>
                        <DescriptionsRow label="В наличии" value={inventory ? countFormatter(inventory.count) : ''} />
                        <DescriptionsRow
                          label="Ответственный сотрудник"
                          value={
                            inventory?.user
                              ? getUserName.fullLastNameAndInitials(
                                  inventory.user.lastName,
                                  inventory.user.firstName,
                                  inventory.user.patronymic,
                                )
                              : ''
                          }
                        />
                        <DescriptionsRow label="Подразделение" value={inventory?.subdivision ? inventory.subdivision.name : ''} />
                      </>
                    )}
                    {isWriteOffInventory && (
                      <>
                        <DescriptionsRow label="Списан в количестве" value={inventory ? countFormatter(inventory.count) : ''} />
                        <DescriptionsRow
                          label="Причина"
                          value={inventory?.reason ? EInventoryActionReasonLabel[inventory.reason as EInventoryActionReason] : ''}
                        />
                        <DescriptionsRow label="Комментарий" value={inventory?.comment ? inventory.comment : ''} />
                      </>
                    )}
                    <DescriptionsRow
                      label="Последнее обновление"
                      value={inventory ? dateFormatter('DD.MM.YYYY в HH:mm:ss', inventory.updatedAt) : ''}
                    />
                    <DescriptionsRow label="" value="" />
                  </Descriptions>
                ),
              },
            ]}
          />

          {!!writeOffRequests?.length && (
            <>
              <Divider marginY={20} />

              <CardCollapse
                keys={['writeOffRequests']}
                items={[
                  {
                    key: 'writeOffRequests',
                    label: (
                      <span className="ff-medium fs-m">
                        Запросы на списание
                        <CountBadge count={writeOffRequests.length} />
                      </span>
                    ),
                    children: writeOffRequests.map((request) => {
                      const message = getInventoryCardNotificationMessage(inventory, request);

                      return (
                        <div key={request.id} className="mb-3">
                          <Notification
                            type="warning"
                            title={`Запрошено списание ${request.count} шт`}
                            description={message}
                            footer={[
                              <Button
                                key="cancel"
                                className="button-sm black"
                                onClick={() => onCancelInventoryActionClick(request.id)}
                              >
                                Отменить запрос
                              </Button>,
                            ]}
                          />
                        </div>
                      );
                    }),
                  },
                ]}
              />
            </>
          )}

          {!!transferRequests?.length && (
            <>
              <Divider marginY={20} />

              <CardCollapse
                keys={['transferRequests']}
                items={[
                  {
                    key: 'transferRequests',
                    label: (
                      <span className="ff-medium fs-m">
                        Запросы на передачу
                        <CountBadge count={transferRequests.length} />
                      </span>
                    ),
                    children: transferRequests.map((request) => {
                      const message = getInventoryCardNotificationMessage(inventory, request);

                      return (
                        <div key={request.id} className="mb-3">
                          <Notification
                            type="success"
                            title={`Запрошена передача ${request.count} шт`}
                            description={message}
                            footer={[
                              <Button
                                key="cancel"
                                className="button-sm black"
                                onClick={() => onCancelInventoryActionClick(request.id)}
                              >
                                Отменить запрос
                              </Button>,
                            ]}
                          />
                        </div>
                      );
                    }),
                  },
                ]}
              />
            </>
          )}

          {!!inventory?.assignees.length && (
            <>
              <Divider marginY={20} />

              <CardCollapse
                keys={['assignees']}
                items={[
                  {
                    key: 'assignees',
                    label: <span className="ff-medium fs-m">Закрепленное оборудование</span>,
                    children: inventory.assignees.map((assignee) => {
                      return (
                        <div key={assignee.id} className="flex align-center justify-between mb-5">
                          <div className="flex align-start">
                            <div className="mr-4">
                              <IconBackground
                                content={<LinkedUser className="icon-linked-user-dark-grey" />}
                                color={EColor.LightGrey}
                              />
                            </div>

                            <div>
                              <div className="ff-regular fs-s">{assignee.name}</div>

                              <span className="ff-regular fs-xs color-dark-grey">{countFormatter(assignee.count)}</span>
                            </div>
                          </div>

                          <InventoryAssigneeUnassignBtn
                            inventoryId={inventory.id}
                            assignee={assignee}
                            disabled={!!inventory?.user && inventory?.user.id !== currentUser?.id}
                            updateAssignee={handleAddInventoryAssignee}
                            removeAssignee={handleRemoveInventoryAssignee}
                          />
                        </div>
                      );
                    }),
                  },
                ]}
              />
            </>
          )}
        </div>

        {/* TODO For future improvement */}
        {/* {!!inventory?.workspacePositions.length && (
          <div className="mb-9">
            <div className="ff-medium fs-m">В карте оснащения</div>

            <Divider marginY={12} />

            <div className="flex align-center wrap mt-7">
              {inventory.workspacePositions.map(({ id, categoryName, workspaceId }) => {
                return (
                  <Button
                    key={id}
                    className="button-chip mr-4 mb-2"
                    onClick={() => navigate(`${ERoute.WorkspaceList}/${workspaceId}`)}
                  >
                    {categoryName}
                  </Button>
                );
              })}
            </div>
          </div>
        )} */}
      </div>
    </Drawer>
  );
};
